'use client';

import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import AppImage from '@/components/ui/AppImage';
import { inquiryService, productService } from '@/lib/api/services';
import {
  WELCOME_OFFER_BROWSE_DELAY_MS,
  completeWelcomeOffer,
  dismissWelcomeOffer,
  markWelcomeOfferShownThisSession,
  shouldOfferWelcomeDialog,
} from '@/lib/welcomeOffer';

export default function WelcomeOfferDialog() {
  const [open, setOpen] = useState(false);
  const [heroImage, setHeroImage] = useState<string>('/assets/images/no_image.png');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (!shouldOfferWelcomeDialog()) return;
      markWelcomeOfferShownThisSession();
      setOpen(true);
    }, WELCOME_OFFER_BROWSE_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadHero() {
      try {
        const featured = await productService.getActivePage({
          page: 1,
          limit: 1,
          featured: true,
        });
        const fromFeatured = featured.items?.[0]?.mainImage;
        if (fromFeatured) {
          if (!cancelled) setHeroImage(fromFeatured);
          return;
        }
        const page = await productService.getActivePage({ page: 1, limit: 1 });
        const image = page.items?.[0]?.mainImage;
        if (image && !cancelled) setHeroImage(image);
      } catch {
        // keep fallback
      }
    }
    loadHero();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDialog();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const closeDialog = () => {
    if (!submitted) dismissWelcomeOffer();
    setOpen(false);
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      next.email = 'Enter a valid email';
    }
    if (!/^[6-9]\d{9}$/.test(mobile.trim())) {
      next.mobile = 'Enter a valid 10-digit mobile number';
    }
    if (!consent) next.consent = 'Please agree to be contacted';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await inquiryService.create({
        customerName: name.trim(),
        mobile: mobile.trim(),
        email: email.trim(),
        productName: 'Guest list offer',
        productCode: 'GUEST-LIST',
        productId: '',
        message:
          'Joined the guest list from the welcome offer. Interested in 5% off on orders above ₹10,000.',
        inquiryDate: today,
        source: 'contact_form',
        status: 'new',
        notes: 'welcome_offer',
      });
      completeWelcomeOffer();
      setSubmitted(true);
      toast.success('You are on the guest list', {
        description: 'We will send the offer details to your inbox.',
      });
    } catch (err: any) {
      toast.error('Could not submit', { description: err?.message || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeDialog();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-offer-title"
        className="flex w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-zinc-100 bg-white shadow-2xl animate-scale-in"
      >
        <div className="relative hidden w-[42%] shrink-0 md:block">
          <AppImage
            src={heroImage}
            alt="Multizone catalog"
            fill
            sizes="320px"
            className="object-cover"
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/55 to-zinc-950/20" />
          <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-lime-200/45 to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center text-white">
            <p className="text-sm font-medium uppercase tracking-[0.22em] text-lime-200">
              Welcome to
            </p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-[0.18em] text-white">MULTIZONE</h2>
            <div className="mt-4 flex items-center gap-2">
              <span className="h-px w-10 bg-lime-200/80" />
              <span className="h-1.5 w-1.5 rotate-45 bg-lime-200" />
              <span className="h-px w-10 bg-lime-200/80" />
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto px-6 py-7 sm:px-8">
          {submitted ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center py-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-lime-200 ring-1 ring-lime-300">
                <span className="text-zinc-950 text-lg font-bold">✓</span>
              </div>
              <h3 className="text-xl font-semibold text-zinc-950">You are on the list</h3>
              <p className="mt-2 max-w-sm text-sm text-zinc-500">
                Thank you. Our team will share the guest-list offer on your email shortly.
              </p>
              <button
                type="button"
                onClick={closeDialog}
                className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600 underline underline-offset-4"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <h3
                id="welcome-offer-title"
                className="text-[1.65rem] leading-tight font-semibold tracking-tight text-zinc-950"
              >
                Join Our Guest List and Get 5% Off
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-500">
                Get{' '}
                <span className="rounded-full bg-lime-200 px-2 py-0.5 text-xs font-bold text-zinc-950 ring-1 ring-lime-300">
                  5% off
                </span>{' '}
                on orders above ₹10,000 just for joining our guest list. Enter your details and we
                will send the offer to your inbox.
              </p>

              <div className="mt-5 space-y-3">
                <div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="input-field"
                    autoComplete="name"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>
                <div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="input-field"
                    autoComplete="email"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                </div>
                <div>
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    placeholder="Mobile number"
                    className="input-field"
                    autoComplete="tel"
                  />
                  {errors.mobile && <p className="mt-1 text-xs text-red-600">{errors.mobile}</p>}
                </div>
              </div>

              <label className="mt-4 flex items-start gap-2.5 text-[11px] leading-relaxed text-zinc-500">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-zinc-300 text-red-600 focus:ring-red-500"
                />
                <span>
                  By checking this box, you agree to receive catalog updates and offer messages from
                  Multizone. You can opt out anytime.
                </span>
              </label>
              {errors.consent && <p className="mt-1 text-xs text-red-600">{errors.consent}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary mt-5 w-full py-3 text-sm font-semibold uppercase tracking-[0.16em]"
              >
                {submitting ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Sending
                  </span>
                ) : (
                  'Submit'
                )}
              </button>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={closeDialog}
                  className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-600 underline underline-offset-4"
                >
                  Close
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
